import type { QrEncoder, QrMatrix } from '@/application/ports/QrEncoder';

/**
 * A real QR encoder, from the library that already provides the reader.
 *
 * `@zxing/library` is what `@zxing/browser` is built on, so encoding costs no
 * new dependency and the writer and the reader cannot disagree about the
 * format. It is imported dynamically because only the print sheet needs it.
 *
 * This matters more than it looks. The prototype draws a decorative matrix of
 * pseudo-random cells: it photographs like a QR code and decodes to nothing. A
 * tray printed with that is a tray a technician cannot scan, which is the one
 * thing the whole system depends on.
 *
 * Error correction level M is the usual choice for a printed label. It survives
 * a scuffed or lightly toner starved print, where L would not, without the
 * module count that H would add to a code printed at 56mm.
 */
export class ZxingQrEncoder implements QrEncoder {
  async encode(value: string): Promise<QrMatrix> {
    const { QRCodeWriter, BarcodeFormat, EncodeHintType, QRCodeDecoderErrorCorrectionLevel } =
      await import('@zxing/library');

    const hints = new Map<unknown, unknown>();
    // Q, a quarter of the symbol recoverable: a smudged or creased tray card still reads.
    // The codes are short, so the symbol stays the smallest version and the modules large.
    hints.set(EncodeHintType.ERROR_CORRECTION, QRCodeDecoderErrorCorrectionLevel.Q);
    // The sheet draws its own quiet zone in CSS padding, so the matrix is bare.
    hints.set(EncodeHintType.MARGIN, 0);

    // Width and height of zero ask for the natural module size rather than a
    // pixel raster, which is what a vector sheet wants.
    const matrix = new QRCodeWriter().encode(
      value,
      BarcodeFormat.QR_CODE,
      0,
      0,
      hints as Map<never, never>,
    );

    const size = matrix.getWidth();
    const modules: boolean[] = [];
    for (let y = 0; y < size; y += 1) {
      for (let x = 0; x < size; x += 1) {
        // BitMatrix.get returns a boolean here, though the underlying encoder
        // works in ones and zeroes. Coerce rather than compare.
        modules.push(Boolean(matrix.get(x, y)));
      }
    }

    return { size, modules };
  }
}
